import React, { useState } from "react";
import { useFormik } from "formik";
import Button from "react-bootstrap/Button";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import * as Yup from "yup";
import api from "../../../../config/URL";
import { toast } from "react-toastify";
import { IoCloseCircleOutline } from "react-icons/io5";
import CloseIcon from "@mui/icons-material/Close";

function BatchTimeEdit({ id, onSuccess, handleMenuClose }) {
  const [show, setShow] = useState(false);
  const [loadIndicator, setLoadIndicator] = useState(false);
  const userName = localStorage.getItem("userName");
  const [isModified, setIsModified] = useState(false);
  const [fields, setFields] = useState([]); // No default field
  const [viewOpen, setViewOpen] = useState(false);
  const [viewData, setViewData] = useState(null);

  const handleViewOpen = async () => {
    try {
      const response = await api.get(`/getBatchForSingleDay/${id}`);
      setViewData(response.data);
      setViewOpen(true);
    } catch (error) {
      toast.error("Failed to fetch batch details");
    }
  };

  const handleViewClose = () => {
    setViewOpen(false);
    setViewData(null);
  };

  const validationSchema = Yup.object({
    batchTimes: Yup.array()
      .of(Yup.string().required("Batch time is required"))
      .min(1, "At least one batch time is required"),
  });

  const formik = useFormik({
    initialValues: {
      batchDay: "",
      batchTimes: [],
      totalStudents: [],
      updatedBy: userName,
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      if (values.batchTimes.length === 0) {
        toast.error("At least one batch time is required");
        return;
      }

      setLoadIndicator(true);

      const payload = {
        ...values,
        totalStudents: values.totalStudents.map((v) =>
          v == null || v === "" ? "0" : v,
        ),
      };

      try {
        const response = await api.put(`/updateBatchDays/${id}`, payload, {
          headers: { "Content-Type": "application/json" },
          skipErrorToast: true,
        });

        if (response.status === 200) {
          toast.success(response.data.message);
          onSuccess();
          handleClose(); // ✅ close ONLY on success
        }
      } catch (error) {
        if (error?.response?.status === 409) {
          toast.warning(error.response.data.message);
          return;
        }
        toast.error("Something went wrong");
      } finally {
        setLoadIndicator(false); // ✅ safe
      }
    },

    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
  });

  const handleClose = () => {
    handleMenuClose();
    setShow(false);
    onSuccess();
  };

  const handleShow = () => {
    setShow(true);
    setIsModified(false);
    getData();
  };

  const addFields = () => {
    setFields([
      ...fields,
      {
        id: null,
        batchTimes: "",
        totalStudents: "0", // ✅ default 0
      },
    ]);

    // also sync with formik
    formik.setFieldValue("batchTimes", [...formik.values.batchTimes, ""]);

    formik.setFieldValue("totalStudents", [
      ...formik.values.totalStudents,
      "0", // ✅ default 0
    ]);
  };

  const deleteFields = (index) => {
    // remove from UI state
    const updatedFields = fields.filter((_, i) => i !== index);
    setFields(updatedFields);

    // remove batch time
    const updatedBatchTimes = formik.values.batchTimes.filter(
      (_, i) => i !== index,
    );

    // remove students count
    const updatedStudents = formik.values.totalStudents.filter(
      (_, i) => i !== index,
    );

    formik.setFieldValue("batchTimes", updatedBatchTimes);
    formik.setFieldValue("totalStudents", updatedStudents);
  };
  const getData = async () => {
    try {
      const response = await api.get(`/getBatchForSingleDay/${id}`);
      const fetchedData = response.data;

      formik.setValues({
        batchDay: fetchedData.batchDay,
        batchTimes: fetchedData.batchTimes,
        totalStudents:
          fetchedData.totalStudents ||
          new Array(fetchedData.batchTimes.length).fill(""),
        updatedBy: userName,
      });

      const updatedFields = fetchedData.batchTimes.map((time, index) => ({
        id: Math.random(), // Use unique ID if not available
        batchTimes: time,
        totalStudents: fetchedData.totalStudents?.[index] || "",
      }));
      setFields(updatedFields);
    } catch (error) {
      console.error("Error fetching data", error?.message);
    }
  };

  return (
    <>
      <p
        style={{
          whiteSpace: "nowrap",
          width: "100%",
        }}
        className=" text-start mb-0 menuitem-style"
        onClick={handleShow}
      >
        Edit
      </p>
      <Dialog
        open={show}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        aria-labelledby="batch-time-edit-dialog"
      >
        <DialogTitle className="headColor">
          Batch Time Edit{" "}
          <IconButton
            aria-label="close"
            onClick={handleClose}
            style={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <form
          onSubmit={formik.handleSubmit}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !formik.isSubmitting) {
              e.preventDefault();
            }
          }}
        >
          <DialogContent>
            <div className="container">
              <div className="row">
                <div className="col-md-12 col-12">
                  <label className="form-label">
                    Day<span className="text-danger">*</span>
                  </label>
                  <input
                    onKeyDown={(e) => e.stopPropagation()}
                    type="text"
                    className={`form-control  ${
                      formik.touched.batchDay && formik.errors.batchDay
                        ? "is-invalid"
                        : ""
                    }`}
                    {...formik.getFieldProps("batchDay")}
                    readOnly
                  />
                  {formik.touched.batchDay && formik.errors.batchDay && (
                    <div className="invalid-feedback">
                      {formik.errors.batchDay}
                    </div>
                  )}
                </div>

                <div className="col-md-12 col-12 mb-2">
                  {/* {fields.map((field, index) => (
                    <div key={index}>
                      <div className="d-flex justify-content-between mt-3">
                        <span>
                          <label className="form-label">
                            Batch Time<span className="text-danger">*</span>
                          </label>
                        </span>
                        <span>
                          <button
                            type="button"
                            className="btn btn-sm pb-2 me-2"
                            onClick={() => deleteFields(field)}
                          >
                            <IoCloseCircleOutline
                              style={{ color: "red", fontSize: "18px" }}
                            />
                          </button>
                        </span>
                      </div>
                      <input
                        onKeyDown={(e) => e.stopPropagation()}
                        type="time"
                        className={`form-control  ${
                          formik.touched.batchTimes?.[index] &&
                          formik.errors.batchTimes?.[index]
                            ? "is-invalid"
                            : ""
                        }`}
                        {...formik.getFieldProps(`batchTimes[${index}]`)}
                      />
                      {formik.touched.batchTimes?.[index] &&
                        formik.errors.batchTimes?.[index] && (
                          <div className="invalid-feedback">
                            {formik.errors.batchTimes[index]}
                          </div>
                        )}
                    </div>
                  ))} */}
                  <label className="form-label mt-4">
                    Batch Details<span className="text-danger">*</span>
                  </label>
                  <div className="d-flex align-items-end">
                    <div style={{ flex: 1 }}>
                      <label
                        className="form-label"
                        style={{ fontSize: "13px", color: "#00000088" }}
                      >
                        Batch Time
                      </label>
                    </div>
                    <div className="pe-3" style={{ flex: 1 }}>
                      <label
                        className="form-label"
                        style={{ fontSize: "13px", color: "#00000088" }}
                      >
                        Students Count
                      </label>
                    </div>
                  </div>
                  {fields.map((field, index) => (
                    <div key={index} className="mb-3">
                      <div className="d-flex align-items-end gap-3">
                        {/* Batch Time */}
                        <div style={{ flex: 1 }}>
                          {/* <label className="form-label">Batch Time</label> */}
                          <input
                            type="time"
                            className={`form-control ${
                              formik.touched.batchTimes?.[index] &&
                              formik.errors.batchTimes?.[index]
                                ? "is-invalid"
                                : ""
                            }`}
                            {...formik.getFieldProps(`batchTimes[${index}]`)}
                          />
                          {formik.touched.batchTimes?.[index] &&
                            formik.errors.batchTimes?.[index] && (
                              <div className="invalid-feedback">
                                {formik.errors.batchTimes[index]}
                              </div>
                            )}
                        </div>

                        {/* Students Count */}
                        <div style={{ flex: 1 }}>
                          {/* <label className="form-label">Students Count</label> */}
                          <input
                            type="number"
                            min="0"
                            className="form-control"
                            value={formik.values.totalStudents[index] || "0"}
                            onChange={(e) => {
                              const value =
                                e.target.value === "" ? "0" : e.target.value;

                              const updatedStudents = [
                                ...formik.values.totalStudents,
                              ];
                              updatedStudents[index] = value;

                              formik.setFieldValue(
                                "totalStudents",
                                updatedStudents,
                              );

                              const updatedFields = [...fields];
                              updatedFields[index].totalStudents = value;
                              setFields(updatedFields);
                            }}
                          />
                        </div>

                        {/* Delete Button */}
                        <div className="d-flex align-items-center">
                          <button
                            type="button"
                            className="btn btn-sm p-0 mb-2"
                            onClick={() => deleteFields(index)}
                          >
                            <IoCloseCircleOutline
                              style={{ color: "red", fontSize: "20px" }}
                            />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  {fields.length === 0 && formik.touched.batchTimes && (
                    <div className="text-danger mt-2">
                      At least one batch time is required
                      <span className="text-danger">*</span>
                    </div>
                  )}
                  <button
                    type="button"
                    className="btn btn-button btn-sm mt-3"
                    onClick={addFields}
                  >
                    Add more
                  </button>
                </div>
              </div>
            </div>
          </DialogContent>
          <DialogActions>
            <button
              type="button"
              className="btn btn-border btn-sm"
              style={{ fontSize: "12px" }}
              onClick={handleClose}
            >
              Cancel
            </button>
            <Button
              type="submit"
              className="btn btn-button btn-sm"
              disabled={loadIndicator}
            >
              {loadIndicator && (
                <span
                  className="spinner-border spinner-border-sm me-2"
                  aria-hidden="true"
                ></span>
              )}
              Update
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
}
export default BatchTimeEdit;
